// 利用者の直接の配置（クリック・ドラッグ・ダブルクリック・右クリックメニュー）でオブジェクトを 1 件作成し、
// 前回の種別と種類ごとの配置サイズを記憶する（lib/placement-size-preference.ts）。
// 貼り付け・取り込み・複製・コネクタからの自動作成はこの経路を通らないため、記憶を変えない。

import type { CanvasObject } from "@refinement-canvas/domain";
import type { PlaceableObjectKind } from "../../lib/last-placed-object-preference";
import { createPlacementSizeOutcome, recordPlacement } from "../../lib/placement-size-preference";
import type { PlacementSizeKey } from "./object-placement-defaults";
import type { ShapeKind } from "./store/canvas-store";
import type { CanvasObjectDraft, CanvasOpDispatcher } from "./use-canvas-op-dispatcher";

/** 直接の配置で記憶する内容。 */
export interface PlacementMemory {
  readonly kind: PlaceableObjectKind;
  readonly shapeKind?: ShapeKind | undefined;
  /**
   * 記憶し直すサイズの単位。withHeight が false のときは幅だけを記憶する（テキストの記憶に高さが無い場合）。
   * undefined は既定サイズで置いた配置で、サイズの記憶を作らない（既定は文字サイズや拡大率で変わるため、
   * 置いた時点の値で固定しない）。
   */
  readonly size?: { readonly key: PlacementSizeKey; readonly withHeight: boolean } | undefined;
}

/**
 * オブジェクトを 1 件作成し、前回の種別とサイズを記憶する。サイズは作成をローカルで受理した時点で、
 * 実際に置いた大きさで楽観保存し、作成の op がサーバに拒否されたら記憶を戻す（後から別の操作で
 * 更新されていれば戻さない）。作成を受理しなかった場合と、送信経路が同期的に拒否した
 * （createObject が戻る前に拒否が届き、作成が巻き戻った）場合は、置けていないため何も記憶せず null を返す
 * （呼び出し側は存在しないオブジェクトを選択・編集しない）。
 */
export function createAndRememberPlacement(
  dispatcher: CanvasOpDispatcher,
  draft: CanvasObjectDraft,
  memory: PlacementMemory
): CanvasObject | null {
  const outcome = createPlacementSizeOutcome();
  const created = dispatcher.createObject(draft, {
    onRejected: outcome.onRejected,
    onSettled: outcome.onSettled,
  });
  if (!created || outcome.isRejected()) return null;
  const size = memory.size;
  outcome.bind(
    recordPlacement({
      kind: memory.kind,
      shapeKind: memory.shapeKind,
      size:
        size === undefined
          ? undefined
          : {
              key: size.key,
              value: size.withHeight ? { w: created.w, h: created.h } : { w: created.w },
            },
    })
  );
  return created;
}
